import { fetchOrganizationAccess } from "./churchOrganizationAccessService";
import { supabase } from "./churchSupabaseClient";

const LOCAL_KEY = "oikos.church.attendance.app";

function today() {
  return new Date().toISOString().slice(0, 10);
}

function readLocalWorkspace() {
  if (typeof window === "undefined") {
    return { attendance: [], families: [], members: [], checkins: [] };
  }

  try {
    const parsed = JSON.parse(window.localStorage.getItem(LOCAL_KEY) || "{}");
    return {
      attendance: Array.isArray(parsed.attendance) ? parsed.attendance : [],
      families: Array.isArray(parsed.families) ? parsed.families : [],
      members: Array.isArray(parsed.members) ? parsed.members : [],
      checkins: Array.isArray(parsed.checkins) ? parsed.checkins : [],
    };
  } catch (error) {
    return { attendance: [], families: [], members: [], checkins: [] };
  }
}

function writeLocalWorkspace(workspace) {
  if (typeof window === "undefined") return workspace;

  try {
    window.localStorage.setItem(LOCAL_KEY, JSON.stringify(workspace));
  } catch (error) {
    console.error("Attendance local save error:", error);
  }

  return workspace;
}

function getSessionToken(user) {
  return user?.churchOrgSession?.sessionToken || user?.churchOrgSession?.session_token || "";
}

async function getAccountId(user) {
  if (user?.churchOrgSession?.account?.id) return user.churchOrgSession.account.id;
  if (!user?.id) return "";

  const access = await fetchOrganizationAccess(user.id, "church");
  return access?.account?.id || "";
}

function normalizeAttendance(row = {}) {
  return {
    id: row.id || "",
    attendanceDate: row.attendance_date || row.attendanceDate || today(),
    worshipCount: Number(row.worship_count ?? row.worshipCount ?? 0),
    classCount: Number(row.class_count ?? row.classCount ?? 0),
    kidsCount: Number(row.kids_count ?? row.kidsCount ?? 0),
    visitorCount: Number(row.visitor_count ?? row.visitorCount ?? 0),
    notes: row.notes || "",
    createdAt: row.created_at || row.createdAt || "",
    updatedAt: row.updated_at || row.updatedAt || "",
  };
}

function normalizeFamily(row = {}) {
  return {
    id: row.id || "",
    familyName: row.family_name || row.familyName || "Family",
  };
}

function normalizeMember(row = {}) {
  return {
    id: row.id || "",
    familyId: row.family_id || row.familyId || "",
    firstName: row.first_name || row.firstName || "",
    lastName: row.last_name || row.lastName || "",
    email: row.email || "",
    phone: row.phone || "",
    role: row.member_role || row.role || "",
    isActive: row.is_active !== false && row.isActive !== false,
  };
}

function normalizeCheckin(row = {}) {
  return {
    id: row.id || "",
    attendanceRecordId: row.attendance_record_id || row.attendanceRecordId || "",
    memberId: row.member_id || row.memberId || "",
    isPresent: row.is_present !== false && row.isPresent !== false,
  };
}

function normalizeWorkspace(payload = {}) {
  return {
    attendance: (payload.attendance || []).map((row) => normalizeAttendance(row)),
    families: (payload.families || []).map((row) => normalizeFamily(row)),
    members: (payload.members || []).map((row) => normalizeMember(row)).filter((member) => member.isActive),
    checkins: (payload.checkins || []).map((row) => normalizeCheckin(row)),
    databaseReady: payload.databaseReady !== false,
    databaseMessage: payload.databaseMessage || "",
  };
}

export async function loadChurchAttendanceAppWorkspace(user) {
  const sessionToken = getSessionToken(user);
  const accountId = await getAccountId(user).catch(() => "");

  if (!sessionToken && !accountId) {
    return normalizeWorkspace(readLocalWorkspace());
  }

  try {
    const { data, error } = await supabase.rpc("church_attendance_app_workspace", {
      p_session_token: sessionToken || "",
      p_account_id: accountId || null,
    });

    if (error) throw error;

    const workspace = normalizeWorkspace(data || {});
    writeLocalWorkspace(workspace);
    return workspace;
  } catch (error) {
    console.error("Attendance workspace load error:", error);
    return normalizeWorkspace(readLocalWorkspace());
  }
}

export async function saveChurchAttendanceAppRecord(user, draft) {
  const sessionToken = getSessionToken(user);
  const accountId = await getAccountId(user).catch(() => "");
  const presentMemberIds = Array.isArray(draft.presentMemberIds) ? draft.presentMemberIds : [];

  if (!sessionToken && !accountId) {
    const local = readLocalWorkspace();
    const record = normalizeAttendance({
      id: draft.id || `attendance-${Date.now()}`,
      attendanceDate: draft.attendanceDate || today(),
      worshipCount: draft.worshipCount,
      classCount: draft.classCount,
      kidsCount: draft.kidsCount,
      visitorCount: draft.visitorCount,
      notes: draft.notes,
    });
    return writeLocalWorkspace({
      ...local,
      attendance: [record, ...local.attendance.filter((item) => item.id !== record.id)],
      checkins: [
        ...local.checkins.filter((item) => item.attendanceRecordId !== record.id),
        ...presentMemberIds.map((memberId) => ({
          id: `${record.id}-${memberId}`,
          attendanceRecordId: record.id,
          memberId,
          isPresent: true,
        })),
      ],
    });
  }

  const { data, error } = await supabase.rpc("church_attendance_app_save", {
    p_session_token: sessionToken || "",
    p_account_id: accountId || null,
    p_record_id: draft.id || null,
    p_attendance_date: draft.attendanceDate || today(),
    p_worship_count: Number(draft.worshipCount || 0),
    p_class_count: Number(draft.classCount || 0),
    p_kids_count: Number(draft.kidsCount || 0),
    p_visitor_count: Number(draft.visitorCount || 0),
    p_notes: draft.notes || "",
    p_present_member_ids: presentMemberIds,
  });

  if (error) throw error;

  const workspace = normalizeWorkspace(data || {});
  writeLocalWorkspace(workspace);
  return workspace;
}

export function getPresentMemberIdsForRecord(workspace, recordId) {
  if (!recordId) return [];
  return (workspace?.checkins || [])
    .filter((item) => item.attendanceRecordId === recordId && item.isPresent)
    .map((item) => item.memberId);
}

export { today as getTodayDate };
